import React, { useContext } from 'react';

import { token } from '@betterment/samba-styled-system/tokens';
import { Text } from '@visx/text';
import { DataContext } from '@visx/xychart';
import { curveMonotoneX } from 'd3-shape';

import { XAxis } from '../axis/XAxis';
import { YAxis } from '../axis/YAxis';
import { Grid } from '../Grid';
import { AreaSeries } from '../series/AreaSeries';
import { createSambaChartTheme } from '../theme';
import { XYChart } from '../XYChart';

import type { TickRendererProps } from '@visx/axis';

import './PlaceholderAreaChart.css';

const placeholderTheme = createSambaChartTheme({
  colors: [token('colors.background.shimmer.highlight')],
});

type DataPoint = {
  x: number;
  y: number;
};

// Mock data extracted from SVG path - key points with varied curves
const mockData: DataPoint[] = [
  { x: 0, y: 0 },
  { x: 2, y: 4.6 },
  { x: 5, y: 5.1 },
  { x: 10, y: 5.6 },
  { x: 15, y: 5.8 },
  { x: 20, y: 6.2 },
  { x: 30, y: 7.1 },
  { x: 40, y: 7.5 },
  { x: 50, y: 7.8 },
  { x: 70, y: 8.9 },
  { x: 90, y: 10.2 },
  { x: 110, y: 11.5 },
  { x: 130, y: 11.8 },
  { x: 150, y: 11.2 },
  { x: 170, y: 10.8 },
  { x: 190, y: 10.3 },
  { x: 210, y: 10.1 },
  { x: 230, y: 11.4 },
  { x: 250, y: 12.1 },
  { x: 270, y: 12.5 },
  { x: 290, y: 12.8 },
  { x: 310, y: 12.9 },
  { x: 330, y: 12.8 },
  { x: 350, y: 13.5 },
  { x: 370, y: 14.2 },
  { x: 390, y: 14.6 },
  { x: 410, y: 14.8 },
  { x: 430, y: 13.8 },
  { x: 450, y: 12.1 },
  { x: 470, y: 10.5 },
  { x: 490, y: 10.8 },
  { x: 510, y: 10.9 },
  { x: 530, y: 12.6 },
  { x: 550, y: 14.5 },
  { x: 570, y: 15.2 },
  { x: 590, y: 15.8 },
  { x: 610, y: 16.2 },
  { x: 630, y: 16.8 },
  { x: 650, y: 16.5 },
];

type Props = {
  height: number;
  width: number;
  variant?: 'loading' | 'empty';
  'aria-label': string;
  'aria-describedby': string;
};

const xAccessor = (d: DataPoint) => d.x;
const yAccessor = (d: DataPoint) => d.y;

const StaticTick = ({ formattedValue, ...props }: TickRendererProps) => {
  const tickHeight = 8;
  const { theme } = useContext(DataContext) ?? {};

  return (
    <Text
      {...props}
      textLength={32}
      lengthAdjust="spacingAndGlyphs"
      verticalAnchor="start"
      style={{
        fill: theme?.colors?.[0],
        fontSize: tickHeight,
        lineHeight: tickHeight,
      }}
    >
      {'\u2588'}
    </Text>
  );
};

/**
 * A placeholder area chart used for loading and empty states.
 *
 * Displays a stylized area chart with mock data. Supports two variants:
 * - 'loading': Shows with shimmer animation for loading states
 * - 'empty': Shows without animation for empty data states
 *
 * @example
 * ```tsx
 * // Loading state
 * <PlaceholderAreaChart
 *   width={600}
 *   height={400}
 *   variant="loading"
 *   aria-label="Chart is loading"
 *   aria-describedby="loading-description"
 * />
 *
 * // Empty state
 * <PlaceholderAreaChart
 *   width={600}
 *   height={400}
 *   variant="empty"
 *   aria-label="No data available"
 *   aria-describedby="empty-description"
 * />
 * ```
 */
export const PlaceholderAreaChart: React.FC<Props> = ({
  width,
  height,
  variant = 'loading',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
}) => {
  // Calculate scales - y must start at 0 for area to sit on bottom
  const xMin = Math.min(...mockData.map(xAccessor));
  const xMax = Math.max(...mockData.map(xAccessor));
  const yMax = Math.max(...mockData.map(yAccessor));

  // Add padding only to top for breathing room
  const yPadding = yMax * 0.1;

  return (
    <div
      className={variant === 'loading' ? 'placeholder-chart-shimmer' : undefined}
      role={variant === 'loading' ? 'status' : undefined}
      aria-busy={variant === 'loading' ? true : undefined}
    >
      <XYChart
        width={width}
        margin={{ right: 0, top: 10, left: 50, bottom: 20 }}
        height={height}
        xScale={{ type: 'linear', domain: [xMin, xMax], zero: false }}
        yScale={{ type: 'linear', domain: [0, yMax + yPadding], zero: false }}
        theme={placeholderTheme}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        captureEvents={false}
      >
        <AreaSeries
          dataKey="placeholder-data"
          data={mockData}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          variant="solid"
          curve={curveMonotoneX}
        />
        <Grid />
        <YAxis tickComponent={StaticTick} />
        <XAxis tickComponent={StaticTick} />
      </XYChart>
    </div>
  );
};
